/**
 * The console's sign-up screen — design "Led AI - Console Sign In", 2a
 * ("Create your account").
 *
 * ## Same door, different room
 *
 * The frame and the brand panel are 1a's, unchanged, so signing in and
 * signing up read as two ways into one product rather than as two products
 * (`authFrame.tsx` holds both, and the reasoning for what is ours and what is
 * Clerk's). Only the form side changes: a step eyebrow, the three providers
 * as icon tiles rather than named buttons, and a terms line under Continue.
 *
 * ## Three things around Clerk's card, not inside it
 *
 * The eyebrow, the terms sentence and the "Already have an account?" row are
 * this file's, because 2a puts them where Clerk's own card has no slot:
 * above the title, and between Continue and the footer. Clerk's footer is
 * hidden here (`main.tsx`, scoped to `[data-auth-frame="sign-up"]`) rather
 * than left to render a second, differently-worded version of the row
 * underneath ours.
 *
 * That means the "Sign in" link is ours to route, which is the honest
 * arrangement anyway: both screens are console pages at console paths, and
 * neither should hand a visitor to Clerk's hosted portal halfway through.
 */

import { SignUp } from "@clerk/clerk-react";
import { AuthFrame, AUTH_APPEARANCE, EYEBROW } from "./authFrame";
import { C } from "../tokens";
import { consoleUrl } from "../basePath";

/**
 * 2a's form, as a diff against 1a's.
 *
 * Only what actually differs is restated — spreading the shared object and
 * overriding three keys keeps one set of measurements for both screens, so a
 * change to the field height or the CTA cannot land on one door and miss the
 * other.
 */
const SIGN_UP_APPEARANCE = {
  ...AUTH_APPEARANCE,
  layout: {
    ...AUTH_APPEARANCE.layout,
    // 2a draws the providers as three icon-only tiles. A new account has no
    // provider it used last, so nothing here needs a name to be recognised —
    // and three named buttons at this width wrap onto two rows.
    socialButtonsVariant: "iconButton" as const,
  },
  elements: {
    ...AUTH_APPEARANCE.elements,
    socialButtons: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: 10,
      marginTop: 0,
      overflow: "visible",
    },
    socialButtonsIconButton: {
      height: 48,
      width: "auto",
      margin: 0,
      borderRadius: 12,
      background: C.surface,
      border: `1px solid ${C.line}`,
      boxShadow: "none",
      cursor: "pointer",
      transition: "background-color 120ms ease, border-color 120ms ease",
      "&:hover": { background: C.sunk, borderColor: C.ink },
      "&:focus-visible": { background: C.sunk, borderColor: C.ink, outline: "none" },
      "&:active": { background: C.faint, transform: "scale(0.985)" },
    },
    // The header sits under OUR eyebrow, so it loses the top margin Clerk
    // would otherwise put between the card's edge and the title.
    header: { ...AUTH_APPEARANCE.elements.header, marginTop: 8 },
  },
};

/** The artboard's terms sentence, under Continue. */
function Terms() {
  const link: React.CSSProperties = { color: C.signInQuiet, fontWeight: 600 };
  return (
    <p
      style={{
        fontSize: 12,
        lineHeight: 1.5,
        color: C.sub2,
        marginTop: 14,
        textAlign: "center",
        textWrap: "pretty",
      }}
    >
      By continuing you agree to the{" "}
      <a href={consoleUrl("/terms")} style={link}>
        Terms
      </a>{" "}
      and{" "}
      <a href={consoleUrl("/privacy")} style={link}>
        Privacy Policy
      </a>
      .
    </p>
  );
}

/** 2a, assembled. */
export function SignUpScreen() {
  return (
    <AuthFrame
      screen="sign-up"
      form={
        <>
          {/* Clerk verifies the email next, which is the second step this
              names. It is OUR label because Clerk's card has no slot above
              its own title. */}
          <div style={{ ...EYEBROW, color: C.goldInk }}>Step 1 of 2</div>
          <SignUp
            routing="hash"
            appearance={SIGN_UP_APPEARANCE}
            fallbackRedirectUrl={consoleUrl("/")}
            signInUrl={consoleUrl("/")}
          />
          <Terms />
          <div
            style={{
              borderTop: `1px solid ${C.hair}`,
              marginTop: 26,
              paddingTop: 18,
              fontSize: 13.5,
              color: C.sub,
            }}
          >
            Already have an account?{" "}
            <a href={consoleUrl("/")} style={{ fontWeight: 700, color: C.goldInk }}>
              Sign in
            </a>
          </div>
        </>
      }
    />
  );
}
